import {
  ArrowRight,
  BadgeCheck,
  Bot,
  Building2,
  CalendarClock,
  Check,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  Headphones,
  Mail,
  Menu,
  MessageSquareText,
  Phone,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Utensils,
  Wrench,
  X
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { submitContactLead, trackEvent } from "./apteva";

export type PageKey = "home" | "industries" | "how-it-works" | "pricing" | "contact";

const navigation: Array<{ label: string; href: string; page: PageKey }> = [
  { label: "Industries", href: "/industries/", page: "industries" },
  { label: "How it works", href: "/how-it-works/", page: "how-it-works" },
  { label: "Pricing", href: "/pricing/", page: "pricing" },
  { label: "Contact", href: "/contact/", page: "contact" }
];

const outcomes = [
  "Answers calls, web chats, and missed-call texts",
  "Books appointments directly into your calendar",
  "Escalates sensitive conversations to your team"
];

const industries = [
  {
    icon: Building2,
    title: "Law offices",
    text: "Qualify new matters, capture conflict-check details, route urgent calls, and schedule consultations without losing billable focus.",
    examples: ["Personal injury intake", "Family law consultations", "Estate planning callbacks"]
  },
  {
    icon: Stethoscope,
    title: "Doctors and clinics",
    text: "Handle appointment requests, insurance questions, reminders, and after-hours routing while keeping clinical judgement with staff.",
    examples: ["New patient intake", "Follow-up booking", "Urgent escalation rules"]
  },
  {
    icon: Headphones,
    title: "Dentists",
    text: "Turn missed calls into booked visits, answer treatment FAQs, and recover cancellations with polite automated follow-up.",
    examples: ["Cleaning requests", "Emergency tooth pain", "Treatment plan callbacks"]
  },
  {
    icon: Utensils,
    title: "Restaurants",
    text: "Take reservation requests, answer opening-hour and menu questions, manage private-event leads, and reduce repetitive phone work.",
    examples: ["Reservation routing", "Group booking intake", "Allergy and menu FAQs"]
  },
  {
    icon: Sparkles,
    title: "Salons and nail studios",
    text: "Book treatments, answer service and price questions, manage cancellations, and keep chairs filled without interrupting appointments.",
    examples: ["Manicure bookings", "Color consultation requests", "Cancellation recovery"]
  },
  {
    icon: Wrench,
    title: "Home services",
    text: "Qualify service calls, capture addresses and job details, route emergencies, and turn missed calls into booked estimates.",
    examples: ["HVAC and plumbing intake", "Emergency dispatch rules", "Estimate scheduling"]
  }
];

const receptionistExamples = [
  {
    key: "law",
    tab: "Law",
    title: "Legal intake receptionist",
    actor: "Prospective client",
    request: "\"I was in an accident and need to speak with someone.\"",
    meta: "After-hours phone call",
    steps: [
      "Captures contact, incident type, date, and urgency",
      "Checks disqualifying criteria and office coverage area",
      "Books a consultation or sends an urgent staff alert"
    ],
    result: "A structured lead arrives in your CRM with the call summary, urgency, and next action.",
    metrics: [
      ["Coverage", "24/7"],
      ["Missed calls", "-60%"],
      ["Staff time", "-8h/wk"]
    ]
  },
  {
    key: "clinic",
    tab: "Clinic",
    title: "Medical front desk assistant",
    actor: "Patient",
    request: "\"Can I book a follow-up for next week?\"",
    meta: "Website chat",
    steps: [
      "Collects reason for visit and preferred times",
      "Applies appointment rules and insurance prompts",
      "Books or queues the request for staff approval"
    ],
    result: "The patient gets a clear next step, and your front desk avoids another repetitive call.",
    metrics: [
      ["Requests handled", "40+/day"],
      ["Response time", "<30s"],
      ["Human review", "Built in"]
    ]
  },
  {
    key: "restaurant",
    tab: "Restaurant",
    title: "Reservation and event receptionist",
    actor: "Guest",
    request: "\"Do you have a table for six tonight?\"",
    meta: "Missed-call SMS",
    steps: [
      "Checks party size, time, seating preference, and allergies",
      "Books simple reservations or sends complex cases to a manager",
      "Captures event leads with budget and date details"
    ],
    result: "More bookings are captured without pulling staff away from service.",
    metrics: [
      ["Booking capture", "+25%"],
      ["Languages", "Multi"],
      ["Setup", "Fast"]
    ]
  }
];

const method = [
  {
    step: "01",
    title: "Map the front desk",
    text: "We review your calls, forms, FAQs, booking rules, escalation paths, and the language your team uses with real customers."
  },
  {
    step: "02",
    title: "Build your receptionist",
    text: "We configure scripts, qualification logic, calendar rules, CRM fields, and handoff criteria around your business."
  },
  {
    step: "03",
    title: "Connect your tools",
    text: "The receptionist connects to phone, chat, email, calendars, booking systems, CRM, and notifications."
  },
  {
    step: "04",
    title: "Launch and improve",
    text: "We review transcripts, tune answers, refine routing, and improve conversion after launch."
  }
];

const pricingPlans = [
  {
    name: "Starter",
    price: "$750 setup",
    monthly: "$99/mo",
    description: "For small offices that need an affordable AI receptionist for website chat and missed-call follow-up.",
    features: [
      "One tailored receptionist workflow",
      "Website chat or missed-call SMS",
      "Lead capture into CRM",
      "Up to 150 AI conversations/mo",
      "Quarterly optimization review"
    ]
  },
  {
    name: "Growth",
    price: "$1,500 setup",
    monthly: "$249/mo",
    description: "For teams that want phone, chat, booking, and richer intake logic without a heavy monthly retainer.",
    featured: true,
    features: [
      "Phone, chat, and SMS coverage",
      "Calendar or booking-system integration",
      "Custom intake forms by industry",
      "Up to 500 AI conversations/mo",
      "Monthly tuning and CRM lead tags"
    ]
  },
  {
    name: "Scale",
    price: "$3,500 setup",
    monthly: "$499/mo",
    description: "For multi-location practices, high-volume teams, or businesses with compliance-heavy handoff rules.",
    features: [
      "Multiple receptionists or locations",
      "Advanced escalation and approval flows",
      "Reporting dashboard and call insights",
      "Up to 1,500 AI conversations/mo",
      "Priority support and custom integrations"
    ]
  }
];

const faqItems = [
  ["Do you replace our receptionist?", "No. The AI handles repetitive intake, FAQs, routing, and booking. Your team keeps control over sensitive, complex, or high-value conversations."],
  ["Can it answer phone calls?", "Yes. We can deploy it on phone, web chat, SMS, email intake, or a combination depending on your plan and tools."],
  ["Can it work with our existing calendar or CRM?", "Yes. We design the workflow around your current tools first, then add integrations where they actually save time."],
  ["Is pricing final?", "The three packages are clear starting points. Final scope depends on call volume, number of locations, integrations, and compliance requirements."]
];

export function pageFromPath(pathname: string): PageKey {
  if (pathname.startsWith("/industries")) return "industries";
  if (pathname.startsWith("/how-it-works")) return "how-it-works";
  if (pathname.startsWith("/pricing")) return "pricing";
  if (pathname.startsWith("/contact")) return "contact";
  return "home";
}

export function App({ page = "home" }: { page?: PageKey }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="site-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="Deskora Reception home">
          <span className="brand-mark">DR</span>
          <span>
            <strong>Deskora Reception</strong>
            <small>Tailored front desk automation</small>
          </span>
        </a>

        <nav className="desktop-nav" aria-label="Main navigation">
          {navigation.map((item) => (
            <a key={item.href} href={item.href} aria-current={page === item.page ? "page" : undefined}>
              {item.label}
            </a>
          ))}
        </nav>

        <a className="nav-cta" href="/pricing/">
          See pricing
          <ChevronRight size={16} aria-hidden="true" />
        </a>

        <button
          className="menu-button"
          type="button"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((value) => !value)}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>

      {menuOpen ? (
        <nav className="mobile-nav" aria-label="Mobile navigation">
          {navigation.map((item) => (
            <a key={item.href} href={item.href} onClick={() => setMenuOpen(false)}>
              {item.label}
            </a>
          ))}
        </nav>
      ) : null}

      {page === "home" ? <HomePage /> : null}
      {page === "industries" ? <IndustriesPage /> : null}
      {page === "how-it-works" ? <HowItWorksPage /> : null}
      {page === "pricing" ? <PricingPage /> : null}
      {page === "contact" ? <ContactPage /> : null}

      <footer className="footer">
        <span>Deskora Reception</span>
        <span>Tailor-made receptionists for service businesses that cannot miss calls.</span>
      </footer>
    </div>
  );
}

function HomePage() {
  return (
    <main id="home">
      <section className="hero">
        <div className="hero-copy">
          <span className="eyebrow">Tailor-made AI receptionists</span>
          <h1 className="hero-title">
            <span>Stop missing calls.</span>
            <span>Start booking more clients.</span>
          </h1>
          <p className="hero-lede">
            We design and launch AI receptionists for law offices, restaurants, dentists, doctors,
            clinics, salons, nail studios, home services, and other appointment-driven businesses.
            Every receptionist is trained around your scripts, your rules, and your tools.
          </p>
          <div className="hero-actions">
            <a className="primary-button" href="/pricing/">
              View packages
              <ArrowRight size={18} aria-hidden="true" />
            </a>
            <a className="secondary-button" href="/contact/">
              Request a demo
            </a>
          </div>
          <ul className="outcome-list" aria-label="Expected results">
            {outcomes.map((item) => (
              <li key={item}>
                <Check size={17} aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <HeroVisual />
      </section>

      <TrustBand />
      <IndustriesOverview />
      <MethodOverview />
      <PricingOverview />
      <section className="lead-band">
        <div>
          <span className="section-kicker">Fast check</span>
          <h2>See if an AI receptionist fits your front desk.</h2>
          <p>Send your call volume, industry, and main bottleneck. We will map the first workflow and pricing fit.</p>
        </div>
        <LeadForm variant="compact" sourceSurface="home-inline" buttonText="Request fit check" />
      </section>
    </main>
  );
}

function IndustriesPage() {
  return (
    <main>
      <PageHero
        kicker="Industries"
        title="Built for front desks with real consequences."
        text="A generic chatbot is not enough when a missed call can mean a lost case, an empty table, an unfilled appointment, an urgent service call, or a frustrated patient. We build around the intake rules of your sector."
      />
      <IndustriesGrid detailed />
      <section className="split-section page-split">
        <div>
          <span className="section-kicker">Not listed?</span>
          <h2>If you book, route, qualify, or triage requests, the pattern still applies.</h2>
          <p>
            We also support accountants, real estate offices, med spas, consultants, repair shops,
            and local service teams with high-value inbound requests.
          </p>
        </div>
        <div className="use-case-list">
          {["Appointment booking", "New lead qualification", "FAQ handling", "Urgent escalation", "CRM notes", "Follow-up reminders"].map((item) => (
            <div className="use-case" key={item}>
              <BadgeCheck size={20} aria-hidden="true" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function HowItWorksPage() {
  return (
    <main>
      <PageHero
        kicker="How it works"
        title="A focused implementation process for one outcome: a working receptionist."
        text="We do not sell broad AI strategy. We map your front desk, build the receptionist, connect it to your tools, and keep tuning it after launch."
      />
      <section className="section method-detail-section">
        <div className="method-detail-list">
          {method.map((item) => (
            <article className="method-detail-card" key={item.step}>
              <span>{item.step}</span>
              <div>
                <h2>{item.title}</h2>
                <p>{item.text}</p>
                <strong>{methodProof(item.step)}</strong>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section className="section expertise-depth">
        <div className="section-heading">
          <span className="section-kicker">Built in</span>
          <h2>Controls your staff will actually trust.</h2>
          <p>
            The receptionist is designed with clear boundaries, escalation rules, CRM history, and
            transcripts so your team can review what happened.
          </p>
        </div>
        <div className="expertise-depth-grid">
          {[
            "Human handoff rules for urgent or sensitive conversations",
            "Approved answer library for common questions",
            "CRM records with tags, call notes, and next actions",
            "Calendar logic for availability, duration, and service type",
            "Launch monitoring and transcript review",
            "No black-box automation for regulated decisions"
          ].map((item) => (
            <div className="use-case" key={item}>
              <Check size={20} aria-hidden="true" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function PricingPage() {
  return (
    <main>
      <PageHero
        kicker="Pricing"
        title="Affordable monthly plans, tailored setup, clear scope."
        text="Start at $99/month and add implementation depth only where it creates value. We confirm fit before launch so the receptionist is useful, measurable, and easy for your team to adopt."
      />
      <PricingCards />
      <section className="lead-band lead-band-dark">
        <div>
          <span className="section-kicker">Pick a package</span>
          <h2>Tell us which plan you want to explore.</h2>
          <p>We will respond with the recommended workflow, integration scope, and implementation timeline.</p>
        </div>
        <LeadForm variant="compact" sourceSurface="pricing-inline" includePlan buttonText="Ask about this package" />
      </section>
      <FAQSection />
    </main>
  );
}

function ContactPage() {
  return (
    <main>
      <PageHero
        kicker="Contact"
        title="Send us your front-desk bottleneck."
        text="Tell us what your receptionist, office manager, or front desk handles every day. We will map the first AI receptionist workflow and the right package."
      />
      <section className="contact-direct">
        <div>
          <span className="section-kicker">CRM intake</span>
          <h2>Request a demo, callback, or implementation quote.</h2>
          <p>
            This form sends your request through our CRM workflow. Include your industry, call volume,
            tools, and the conversations you want the AI receptionist to handle first.
          </p>
          <div className="contact-options">
            <a href="mailto:contact@deskorareception.com">
              <Mail size={18} aria-hidden="true" />
              contact@deskorareception.com
            </a>
            <a href="/pricing/">
              <CalendarClock size={18} aria-hidden="true" />
              Compare packages
            </a>
          </div>
        </div>
        <LeadForm variant="full" sourceSurface="contact-page" includePlan buttonText="Send request" />
      </section>
    </main>
  );
}

function PageHero({ kicker, title, text }: { kicker: string; title: string; text: string }) {
  return (
    <section className="page-hero">
      <span className="section-kicker">{kicker}</span>
      <h1>{title}</h1>
      <p>{text}</p>
    </section>
  );
}

function HeroVisual() {
  const [activeKey, setActiveKey] = useState(receptionistExamples[0].key);
  const activeExample = receptionistExamples.find((example) => example.key === activeKey) ?? receptionistExamples[0];

  return (
    <div className="hero-visual support-agent-card" aria-label="AI receptionist examples">
      <div className="agent-header">
        <div>
          <span>Live workflow example</span>
          <strong>{activeExample.title}</strong>
        </div>
        <span className="agent-status">Online</span>
      </div>

      <div className="agent-tabs" role="tablist" aria-label="Choose an AI receptionist example">
        {receptionistExamples.map((example) => (
          <button
            key={example.key}
            type="button"
            role="tab"
            id={`agent-tab-${example.key}`}
            aria-controls={`agent-panel-${example.key}`}
            aria-selected={activeExample.key === example.key}
            className={activeExample.key === example.key ? "active" : undefined}
            onClick={() => {
              setActiveKey(example.key);
              trackEvent("receptionist_example_changed", { industry: example.key });
            }}
          >
            {example.tab}
          </button>
        ))}
      </div>

      {receptionistExamples.map((example) => {
        const isActive = activeExample.key === example.key;

        return (
          <div
            className="agent-example-panel"
            id={`agent-panel-${example.key}`}
            key={example.key}
            role="tabpanel"
            aria-labelledby={`agent-tab-${example.key}`}
            hidden={!isActive}
          >
            <div className="compact-flow">
              <section className="compact-block input-block">
                <span className="flow-label">1. Inbound</span>
                <div className="input-message compact-message">
                  <Phone size={18} aria-hidden="true" />
                  <div>
                    <strong>{example.actor}</strong>
                    <p>{example.request}</p>
                    <small>{example.meta}</small>
                  </div>
                </div>
              </section>

              <section className="compact-block agent-animation-block">
                <span className="flow-label">2. AI receptionist</span>
                <div className="agent-animation">
                  <div className="agent-avatar" aria-hidden="true">
                    <Bot size={26} />
                    <span />
                  </div>
                  <div className="animated-step-panel" aria-label="Receptionist workflow steps">
                    {example.steps.map((step, index) => (
                      <div className={`animated-step animated-step-${["one", "two", "three"][index]}`} key={step}>
                        <ClipboardCheck size={17} aria-hidden="true" />
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="compact-block result-block">
                <span className="flow-label">3. Outcome</span>
                <div className="agent-reply compact-reply">
                  <p>{example.result}</p>
                </div>
              </section>
            </div>

            <div className="impact-grid" aria-label="Business impact">
              {example.metrics.map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>

            <div className="agent-result">
              <ShieldCheck size={18} aria-hidden="true" />
              Human handoff stays built into the workflow.
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrustBand() {
  const channels = [
    { icon: Phone, label: "Phone" },
    { icon: MessageSquareText, label: "Web chat" },
    { icon: Mail, label: "Email intake" },
    { icon: CalendarClock, label: "Booking" }
  ];

  return (
    <section className="trust-band" aria-label="Receptionist channels">
      {channels.map(({ icon: ChannelIcon, label }) => {
        return (
          <div key={label}>
            <ChannelIcon size={21} aria-hidden="true" />
            <span>{label}</span>
          </div>
        );
      })}
    </section>
  );
}

function IndustriesOverview() {
  return (
    <section className="section" id="industries">
      <div className="section-heading">
        <span className="section-kicker">Focused markets</span>
        <h2>Receptionists for businesses where every inquiry has value.</h2>
        <p>
          We start with sectors that have repetitive inbound conversations, clear booking rules, and
          expensive missed opportunities.
        </p>
      </div>
      <IndustriesGrid />
    </section>
  );
}

function IndustriesGrid({ detailed = false }: { detailed?: boolean }) {
  return (
    <section className={detailed ? "section" : undefined}>
      <div className="service-grid">
        {industries.map((industry) => {
          const Icon = industry.icon;
          return (
            <article className="service-card service-card-detailed" key={industry.title}>
              <Icon size={28} aria-hidden="true" />
              <h3>{industry.title}</h3>
              <p>{industry.text}</p>
              {detailed ? (
                <ul className="compact-list">
                  {industry.examples.map((example) => (
                    <li key={example}>{example}</li>
                  ))}
                </ul>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function MethodOverview() {
  return (
    <section className="section method-section" id="method">
      <div className="section-heading">
        <span className="section-kicker">Process</span>
        <h2>From call map to live receptionist.</h2>
      </div>
      <div className="timeline">
        {method.map((item) => (
          <article className="timeline-item" key={item.step}>
            <span>{item.step}</span>
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function PricingOverview() {
  return (
    <section className="split-section" id="pricing">
      <div>
        <span className="section-kicker">Pricing</span>
        <h2>Start with a package, then tailor the receptionist.</h2>
        <p>
          Clear starting prices keep the project grounded. The final build is adjusted to your volume,
          integrations, and handoff requirements.
        </p>
        <a className="text-link" href="/pricing/">
          Compare packages
          <ArrowRight size={17} aria-hidden="true" />
        </a>
      </div>
      <div className="use-case-list">
        {pricingPlans.map((plan) => (
          <div className="use-case" key={plan.name}>
            <BadgeCheck size={20} aria-hidden="true" />
            <span>{plan.name}: {plan.price} + {plan.monthly}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function PricingCards() {
  return (
    <section className="section pricing-section">
      <div className="pricing-grid">
        {pricingPlans.map((plan) => (
          <article className={`pricing-card ${plan.featured ? "pricing-card-featured" : ""}`} key={plan.name}>
            <div className="pricing-card-head">
              <span className={`pricing-badge ${plan.featured ? "" : "pricing-badge-placeholder"}`}>
                {plan.featured ? "Most popular" : "Package"}
              </span>
              <h2>{plan.name}</h2>
              <p>{plan.description}</p>
            </div>
            <div className="price-line">
              <strong>{plan.price}</strong>
              <span>{plan.monthly}</span>
            </div>
            <ul>
              {plan.features.map((feature) => (
                <li key={feature}>
                  <Check size={18} aria-hidden="true" />
                  {feature}
                </li>
              ))}
            </ul>
            <a className={`${plan.featured ? "primary-button" : "secondary-button"} pricing-card-button`} href={`/contact/?plan=${encodeURIComponent(plan.name)}`}>
              Discuss {plan.name}
              <ArrowRight size={17} aria-hidden="true" />
            </a>
          </article>
        ))}
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="section faq-section">
      <div className="section-heading">
        <span className="section-kicker">Questions</span>
        <h2>What buyers usually ask first.</h2>
      </div>
      <div className="faq-grid">
        {faqItems.map(([question, answer]) => (
          <article className="faq-card" key={question}>
            <h3>{question}</h3>
            <p>{answer}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function methodProof(step: string) {
  const proof: Record<string, string> = {
    "01": "Output: call map, FAQ list, escalation matrix, and first workflow scope.",
    "02": "Output: receptionist scripts, lead fields, booking rules, and review prompts.",
    "03": "Output: connected phone/chat channel, CRM logging, notifications, and calendar path.",
    "04": "Output: launch review, transcript tuning, conversion notes, and next improvements."
  };
  return proof[step] || "";
}

function LeadForm({
  variant,
  sourceSurface,
  includePlan = false,
  buttonText
}: {
  variant: "compact" | "full";
  sourceSurface: string;
  includePlan?: boolean;
  buttonText: string;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [hasStartedForm, setHasStartedForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");

  useEffect(() => {
    if (!includePlan || typeof window === "undefined") return;
    const plan = new URLSearchParams(window.location.search).get("plan") || "";
    if (pricingPlans.some((item) => item.name === plan)) {
      setSelectedPlan(plan);
    }
  }, [includePlan]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const honeypot = String(data.get("_honey") || "");
    if (honeypot.trim()) return;

    setIsSubmitting(true);
    setSubmitStatus("idle");

    const payload = {
      name: String(data.get("name") || ""),
      email: String(data.get("email") || ""),
      company: String(data.get("company") || ""),
      phone: String(data.get("phone") || ""),
      companySize: String(data.get("company_size") || ""),
      priorityNeed: String(data.get("priority_need") || ""),
      message: String(data.get("message") || ""),
      industry: String(data.get("industry") || ""),
      plan: String(data.get("plan") || ""),
      preferredContact: String(data.get("preferred_contact") || ""),
      appointmentVolume: String(data.get("appointment_volume") || ""),
      sourceSurface,
      pagePath: typeof window === "undefined" ? "/contact/" : window.location.pathname,
    };

    trackEvent("ai_receptionist_lead_submit", {
      source_surface: sourceSurface,
      industry: payload.industry,
      plan: payload.plan,
      preferred_contact: payload.preferredContact,
    });

    try {
      await submitContactLead(payload);
      trackEvent("ai_receptionist_lead_success", {
        source_surface: sourceSurface,
        industry: payload.industry,
        plan: payload.plan,
      });
      setSubmitStatus("success");
      form.reset();
      setHasStartedForm(false);
    } catch (error) {
      console.error("Lead request failed:", error);
      trackEvent("ai_receptionist_lead_error", {
        source_surface: sourceSurface,
        industry: payload.industry,
        plan: payload.plan,
      });
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleFormFocus() {
    if (hasStartedForm) return;
    setHasStartedForm(true);
    trackEvent("ai_receptionist_lead_started", {
      source_surface: sourceSurface,
      page_path: typeof window === "undefined" ? "/contact/" : window.location.pathname,
    });
  }

  return (
    <form className={`contact-form lead-form-${variant}`} onSubmit={handleSubmit} onFocusCapture={handleFormFocus}>
      <input className="form-honey" type="text" name="_honey" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <label>
        Name
        <input name="name" type="text" autoComplete="name" placeholder="First and last name" required />
      </label>

      <label>
        Work email
        <input name="email" type="email" autoComplete="email" placeholder="name@business.com" required />
      </label>

      <label>
        Business
        <input name="company" type="text" autoComplete="organization" placeholder="Your business name" required />
      </label>

      <label>
        Phone
        <input name="phone" type="tel" autoComplete="tel" placeholder="+1 555 123 4567" />
      </label>

      <label>
        Industry
        <select name="industry" required defaultValue="">
          <option value="" disabled>
            Select
          </option>
          <option value="Law office">Law office</option>
          <option value="Doctor or clinic">Doctor or clinic</option>
          <option value="Dental office">Dental office</option>
          <option value="Restaurant">Restaurant</option>
          <option value="Salon or nail studio">Salon or nail studio</option>
          <option value="Home services">Home services</option>
          <option value="Other service business">Other service business</option>
        </select>
      </label>

      {includePlan ? (
        <label>
          Package
          <select name="plan" value={selectedPlan} onChange={(event) => setSelectedPlan(event.currentTarget.value)}>
            <option value="">Recommend one</option>
            {pricingPlans.map((plan) => (
              <option value={plan.name} key={plan.name}>
                {plan.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label>
        Request volume
        <select name="appointment_volume" defaultValue="">
          <option value="">Select</option>
          <option value="Under 25/week">Under 25/week</option>
          <option value="25-100/week">25-100/week</option>
          <option value="100-300/week">100-300/week</option>
          <option value="300+/week">300+/week</option>
        </select>
      </label>

      <label>
        Preferred contact
        <select name="preferred_contact" defaultValue="">
          <option value="">Any</option>
          <option value="Email">Email</option>
          <option value="Phone">Phone</option>
          <option value="Video call">Video call</option>
        </select>
      </label>

      <label className="full-field">
        What should the AI receptionist handle first?
        <textarea
          name="message"
          rows={variant === "full" ? 6 : 4}
          required
          placeholder="Example: missed calls after 5pm, new client intake, table reservations, appointment rescheduling, insurance questions"
        />
      </label>

      <input name="company_size" type="hidden" value="" readOnly />
      <input name="priority_need" type="hidden" value="AI receptionist" readOnly />

      {submitStatus === "success" ? (
        <p className="form-alert form-alert-success full-field" role="status">
          Thanks. Your request was sent to our CRM workflow.
        </p>
      ) : null}

      {submitStatus === "error" ? (
        <p className="form-alert form-alert-error full-field" role="alert">
          The CRM request failed. Email contact@deskorareception.com and we will follow up.
        </p>
      ) : null}

      <button className="primary-button full-field" type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Sending" : buttonText}
        <ArrowRight size={18} aria-hidden="true" />
      </button>

      <p className="form-note">
        We use your details only to reply about your AI receptionist workflow.
      </p>
    </form>
  );
}
